import {
    formatShortUtcOffset,
    getTimezoneLabel,
    resolveTimezoneId,
    TFullTimezone
} from '@honeybook/i18n';
import type { HBTimezoneFormat } from './HBTimezone.types';

/** What HBTimezone renders: the tag text, plus the full label when there is one. */
export interface HBTimezoneDisplay {
    /** Text inside the tag — `PDT`, `GMT−7`, or the raw input for an unknown zone. */
    text: string;
    /** Full label for the tooltip and accessible name, or `null` when there is none. */
    label: string | null;
}

/**
 * U+2212 MINUS SIGN. A hyphen-minus is narrower than `+` and screen readers read
 * it as "dash" or skip it; the real minus sign lines `GMT−7` up with `GMT+9` and
 * is read as "minus".
 */
const MINUS_SIGN = '−';

function toInstant(dateMs: number | null): Date {
    return dateMs === null ? new Date() : new Date(dateMs);
}

/**
 * `GMT-7` → `GMT−7`, `GMT+5:30` unchanged, `GMT+0` → `GMT`. Returns `''` for
 * anything else, e.g. a letter abbreviation like `PDT`.
 */
function normalizeGmtOffset(gmt: string): string {
    const match = /^GMT(?:([+-])(\d{1,2}(?::\d{2})?))?$/.exec(gmt);

    if (!match) {
        return '';
    }

    const [, sign, offset] = match;

    if (!sign || /^0+(?::0+)?$/.test(offset)) {
        return 'GMT';
    }

    return `GMT${sign === '-' ? MINUS_SIGN : '+'}${offset}`;
}

/**
 * Both formats read `Intl` with the fixed `en-US` locale, never the browser's, so a
 * Hebrew or Chinese browser still gets `PDT` / `GMT+9` rather than locale-specific
 * text.
 */
function formatOffset(timezoneId: string, instant: Date): string {
    try {
        return normalizeGmtOffset(
            formatShortUtcOffset(timezoneId as TFullTimezone, instant)
        );
    } catch {
        return '';
    }
}

/**
 * The zone's common `en-US` abbreviation (`PDT`, `EST`, `GMT`). `Intl` has none for
 * most zones outside the US and spells the offset instead (`GMT+9`), which is
 * normalized to the `offset` format's text (`GMT−3`).
 */
function formatAbbreviation(timezoneId: string, instant: Date): string {
    try {
        const name =
            new Intl.DateTimeFormat('en-US', {
                timeZone: timezoneId,
                timeZoneName: 'short'
            })
                .formatToParts(instant)
                .find(part => part.type === 'timeZoneName')?.value ?? '';

        return /^GMT[+-]/.test(name) ? normalizeGmtOffset(name) || name : name;
    } catch {
        return '';
    }
}

/**
 * Resolves a timezone value to the text HBTimezone shows.
 *
 * `dateMs` is a millisecond timestamp rather than a `Date` so callers can pass a
 * primitive that memoizes; `null` means "now".
 *
 * Returns `null` for an empty value. A value that resolves to no zone, or a zone
 * `Intl` cannot format, comes back as `{ text: value, label: null }`.
 */
export function getTimezoneDisplay(
    timezone: string | null | undefined,
    dateMs: number | null,
    format: HBTimezoneFormat
): HBTimezoneDisplay | null {
    if (!timezone) {
        return null;
    }

    const timezoneId = resolveTimezoneId(timezone, timezone);
    const unresolved = { text: timezone, label: null };

    if (!timezoneId) {
        return unresolved;
    }

    const instant = toInstant(dateMs);
    const text =
        format === 'offset'
            ? formatOffset(timezoneId, instant)
            : formatAbbreviation(timezoneId, instant);

    if (!text) {
        return unresolved;
    }

    const label = getTimezoneLabel(timezoneId);

    return { text, label: label && label !== text ? label : null };
}
